import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('nl-NL') : '—');

interface TaxRate { id: string; soort: string; percentage: number; toepassing: string; geldig_vanaf: string; }

/** Financieel → Instellingen: globale financiële instellingen (belastingtarieven).
 *  De verdeelsleutel staat onder Financieel → Verrekeningen; pakket-/pricingconfig per collectief
 *  staat bij de collectiviteit zelf. */
export const AdminFinancieelInstellingenPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [taxes, setTaxes] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [soort, setSoort] = useState('ipt');
  const [pct, setPct] = useState('');
  const [toep, setToep] = useState('nl_debiteur');
  const [vanaf, setVanaf] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/api/settlement/tax-rates`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setTaxes).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/settlement/tax-rates`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ soort, percentage: +pct, toepassing: toep, geldig_vanaf: vanaf }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Opslaan mislukt'); }
      setPct(''); setVanaf(''); notify.success('Belastingtarief opgeslagen'); load();
    } catch (err) { notify.error(err instanceof Error ? err.message : 'Mislukt'); }
  };

  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none text-sm';

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-mfb-blue">Financiële instellingen</h1>
        <p className="text-sm text-slate-500">
          Belastingtarieven (assurantiebelasting &amp; btw) — versiebeheerd met ingangsdatum, live gebruikt door de afreken-engine.
          De verdeelsleutel staat onder <button onClick={() => navigate('/backoffice/financieel')} className="font-semibold text-mfb-orange hover:underline">Financieel → Verrekeningen</button>.
        </p>
      </div>

      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Belastingtarieven</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 h-fit space-y-4">
          <h3 className="font-semibold text-mfb-blue">Nieuw tarief</h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Soort</label>
            <select value={soort} onChange={(e) => setSoort(e.target.value)} className={inp + ' bg-white'}>
              <option value="ipt">Assurantiebelasting (IPT)</option><option value="btw">Btw</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Percentage</label>
            <input type="number" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)} className={inp} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Toepassing</label>
            <select value={toep} onChange={(e) => setToep(e.target.value)} className={inp + ' bg-white'}>
              <option value="nl_debiteur">Alleen NL-debiteur</option><option value="altijd">Altijd</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ingangsdatum</label>
            <input type="date" value={vanaf} onChange={(e) => setVanaf(e.target.value)} className={inp} required />
          </div>
          <button className="w-full py-2.5 bg-mfb-orange hover:bg-mfb-orange-dark text-white font-semibold rounded-lg transition">Tarief vastleggen</button>
        </form>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Soort', 'Percentage', 'Toepassing', 'Vanaf'].map((h) => (
                <th key={h} className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={4} className="px-5 py-4 text-slate-400">Laden…</td></tr>
              ) : taxes.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-4 text-slate-400">Nog geen tarief.</td></tr>
              ) : taxes.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-5 py-2 font-medium text-mfb-blue uppercase">{t.soort}</td>
                  <td className="px-5 py-2 text-slate-700">{t.percentage}%</td>
                  <td className="px-5 py-2 text-slate-600">{t.toepassing === 'nl_debiteur' ? 'Alleen NL-debiteur' : 'Altijd'}</td>
                  <td className="px-5 py-2 text-slate-600">{fmtDate(t.geldig_vanaf)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminFinancieelInstellingenPage;
